import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  MessageSquare, 
  Send, 
  User, 
  History, 
  Calendar, 
  Clock,
  Lightbulb,
  ClipboardList,
  BookOpen
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OneOnOne90 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [aplicadorNome, setAplicadorNome] = useState('');
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  
  const [formData, setFormData] = useState({
    para_usuario_id: '',
    data_hora: '',
    competencia_tag: '',
    motivo: '',
    topicos: '',
    proximos_passos: ''
  });

  const competenciaOptions = [
    { value: '', label: 'Selecione uma competência' },
    { value: 'lideranca', label: 'Liderança' },
    { value: 'comunicacao', label: 'Comunicação' },
    { value: 'trabalho_equipe', label: 'Trabalho em Equipe' },
    { value: 'resolucao_problemas', label: 'Resolução de Problemas' },
    { value: 'inovacao', label: 'Inovação' },
    { value: 'comportamento', label: 'Comportamento' },
    { value: 'outros', label: 'Outros' }
  ];

  useEffect(() => {
    carregarDadosIniciais();
  }, [user]);

  const carregarDadosIniciais = async () => {
    await carregarFuncionarios();
    await carregarNomeAplicador();
    await carregarHistorico();
  };

  const carregarNomeAplicador = async () => {
    try {
      if (!user) {
        setAplicadorNome('Usuário não identificado');
        return;
      }
      
      if (user.name) {
        setAplicadorNome(user.name);
        return;
      }
      
      if (user.employee_name) {
        setAplicadorNome(user.employee_name);
        return;
      }
      
      if (user.employee_id) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/api/employees/${user.employee_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const nome = response.data.nome_completo || response.data.nome;
          if (nome) {
            setAplicadorNome(nome);
            return;
          }
        } catch (error) {
          console.log('Erro ao buscar por employee_id:', error);
        }
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const employees = response.data || [];
        const employeeFound = employees.find(emp => 
          emp.email === user.email || emp.user_email === user.email
        );
        
        if (employeeFound) {
          const nome = employeeFound.nome_completo || employeeFound.nome;
          setAplicadorNome(nome || user.email);
        } else {
          setAplicadorNome(user.email || 'Nome não disponível');
        }
      } catch (error) {
        console.error('Erro ao carregar nome do aplicador:', error);
        setAplicadorNome(user.email || 'Nome não disponível');
      }
    } catch (error) {
      console.error('Erro geral ao carregar nome do aplicador:', error);
      setAplicadorNome('Nome não disponível');
    }
  };

  const carregarFuncionarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const carregarHistorico = async () => {
    try {
      setLoadingHistorico(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback90/1on1/historico`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorico(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setHistorico([]);
    } finally {
      setLoadingHistorico(false);
    }
  };

  const enviarFeedback = async () => {
    if (!formData.para_usuario_id || !formData.data_hora || !formData.motivo || !formData.topicos || !formData.proximos_passos) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/feedback90/1on1`,
        {
          ...formData,
          de_usuario_id: user.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Check-in 1:1 enviado com sucesso!');

      setFormData({
        para_usuario_id: '',
        data_hora: '',
        competencia_tag: '',
        motivo: '',
        topicos: '',
        proximos_passos: ''
      });

      await carregarHistorico();
    } catch (error) {
      console.error('Erro ao enviar check-in:', error);
      toast.error('Erro ao enviar check-in. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <MessageSquare className="h-8 w-8 mr-3 text-blue-600" />
          Check-ins 1:1
        </h1>
        <p className="text-gray-600 mt-1">
          Conversas individuais para desenvolvimento e alinhamento
        </p>
      </div>

      {/* Campo Aplicador */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Aplicador</p>
              <p className="text-lg font-semibold text-gray-900">{aplicadorNome || 'Carregando...'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="feedback" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="feedback" className="flex items-center space-x-2">
            <Send className="h-4 w-4" />
            <span>Feedback 1:1</span>
          </TabsTrigger>
          <TabsTrigger value="historico" className="flex items-center space-x-2">
            <History className="h-4 w-4" />
            <span>Histórico</span>
          </TabsTrigger>
          <TabsTrigger value="dicas" className="flex items-center space-x-2">
            <Lightbulb className="h-4 w-4" />
            <span>Dicas Práticas</span>
          </TabsTrigger>
          <TabsTrigger value="roteiro" className="flex items-center space-x-2">
            <ClipboardList className="h-4 w-4" />
            <span>Roteiro</span>
          </TabsTrigger>
        </TabsList>

        {/* Aba Feedback 1:1 */}
        <TabsContent value="feedback" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Novo Check-in 1:1</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Data e Hora */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data e Hora do 1:1 *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.data_hora}
                    onChange={(e) => setFormData({ ...formData, data_hora: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Para Quem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Para Quem? *
                </label>
                <select
                  value={formData.para_usuario_id}
                  onChange={(e) => setFormData({ ...formData, para_usuario_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione um funcionário</option>
                  {funcionarios.map((func) => (
                    <option key={func.id} value={func.id}>
                      {func.nome || func.nome_completo}
                    </option>
                  ))}
                </select>
              </div>

              {/* Competência */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Competência (opcional)
                </label>
                <select
                  value={formData.competencia_tag}
                  onChange={(e) => setFormData({ ...formData, competencia_tag: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {competenciaOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* O que motivou a solicitar o 1:1 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  O que motivou a solicitar o 1:1? *
                </label>
                <textarea
                  value={formData.motivo}
                  onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                  rows="3"
                  placeholder="Descreva o motivo para este check-in..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.motivo.length} caracteres
                </p>
              </div>

              {/* Tópicos importantes do 1:1 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tópicos importantes do 1:1 *
                </label>
                <textarea
                  value={formData.topicos}
                  onChange={(e) => setFormData({ ...formData, topicos: e.target.value })}
                  rows="4"
                  placeholder="Liste os principais tópicos abordados..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.topicos.length} caracteres
                </p>
              </div>

              {/* Próximos passos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Próximos passos *
                </label>
                <textarea
                  value={formData.proximos_passos}
                  onChange={(e) => setFormData({ ...formData, proximos_passos: e.target.value })}
                  rows="3"
                  placeholder="Defina os próximos passos e compromissos..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.proximos_passos.length} caracteres
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => navigate('/feedback/90')}>
                  Cancelar
                </Button>
                <Button
                  onClick={enviarFeedback}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Enviando...' : 'Enviar Check-in'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Histórico */}
        <TabsContent value="historico" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <History className="h-5 w-5 text-purple-600" />
                <span>Histórico de Check-ins 1:1</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingHistorico ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Carregando histórico...</p>
                </div>
              ) : historico.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum check-in realizado ainda</h3>
                  <p className="text-gray-600">Os check-ins 1:1 que você realizar aparecerão aqui.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historico.map((checkin) => (
                    <div key={checkin.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <MessageSquare className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <Badge variant="outline" className="text-blue-700 border-blue-300">
                              Check-in 1:1
                            </Badge>
                            {checkin.competencia_tag && (
                              <Badge variant="secondary" className="ml-2">
                                {checkin.competencia_tag}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {checkin.data_hora 
                                ? new Date(checkin.data_hora).toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : checkin.criado_em
                                ? new Date(checkin.criado_em).toLocaleDateString('pt-BR')
                                : 'Data não disponível'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-3 space-y-2">
                        <p className="text-sm text-gray-600">
                          <strong>Aplicador:</strong> {checkin.de_usuario_nome || aplicadorNome || 'Nome não disponível'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Para:</strong> {checkin.para_usuario_nome || 'Nome não disponível'}
                        </p>
                        
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Motivo:</p>
                          <p className="text-sm text-gray-700">{checkin.motivo}</p>
                        </div>
                        
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Tópicos:</p>
                          <p className="text-sm text-gray-700">{checkin.topicos}</p>
                        </div>
                        
                        <div className="mt-2 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Próximos Passos:</p>
                          <p className="text-sm text-gray-700">{checkin.proximos_passos}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Dicas Práticas */}
        <TabsContent value="dicas" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lightbulb className="h-5 w-5 text-yellow-600" />
                <span>Dicas Práticas</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                Aqui estão dicas práticas e estratégicas para orientar você a conduzir um feedback 1:1 eficaz, com base em metodologias de desenvolvimento humano, psicologia organizacional e boas práticas de liderança:
              </p>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3 flex items-center">
                🎯 1. Preparação antes da conversa
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Defina o objetivo claro</strong> — saiba o que quer alcançar: desenvolver, corrigir, reconhecer ou alinhar expectativas.</li>
                <li><strong>Colete fatos, não percepções</strong> — traga exemplos concretos de comportamentos observáveis, evitando julgamentos ou suposições.</li>
                <li><strong>Analise o contexto</strong> — reflita sobre o momento do colaborador (fase de carreira, desafios pessoais, entregas recentes).</li>
                <li><strong>Escolha o ambiente adequado</strong> — local reservado, sem interrupções, criando segurança psicológica.</li>
                <li><strong>Planeje o tempo</strong> — reserve de 30 a 60 minutos, e nunca "em cima da hora".</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3 flex items-center">
                💬 2. Condução da conversa
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Comece com acolhimento</strong> — quebre o gelo e demonstre interesse genuíno ("Como você tem se sentido em relação às suas entregas?").</li>
                <li><strong>Use a escuta ativa</strong> — permita que o colaborador fale antes de trazer seus pontos.</li>
                <li><strong>Dê feedbacks equilibrados</strong> — mescle pontos fortes e oportunidades de melhoria, mantendo uma proporção de 2:1 (dois reconhecimentos para uma melhoria).</li>
                <li><strong>Foque em comportamentos, não em pessoas</strong> — "Notei que nas últimas reuniões você interrompeu colegas" (comportamento), em vez de "Você é impaciente".</li>
                <li><strong>Evite o efeito sanduíche forçado</strong> — o colaborador pode perceber manipulação; seja autêntico na transição entre os pontos.</li>
                <li><strong>Explore causas, não apenas sintomas</strong> — pergunte "O que você acha que tem dificultado isso?" ou "Como posso te apoiar?".</li>
                <li><strong>Defina compromissos concretos</strong> — crie ações específicas, mensuráveis e com prazos (modelo SMART).</li>
                <li><strong>Feche de forma positiva e motivadora</strong> — reafirme a confiança e mostre apoio ("Acredito no seu potencial de evoluir nisso.").</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3 flex items-center">
                🧠 3. Pós-feedback
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Registre os pontos discutidos</strong> — de forma confidencial, para acompanhar a evolução e subsidiar futuros feedbacks ou PDIs.</li>
                <li><strong>Acompanhe com constância</strong> — retome o tema nas próximas 1:1s e reconheça avanços.</li>
                <li><strong>Dê autonomia</strong> — permita que o colaborador assuma responsabilidade por suas melhorias.</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Roteiro */}
        <TabsContent value="roteiro" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ClipboardList className="h-5 w-5 text-green-600" />
                <span>Roteiro</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">🗓️ Roteiro de Feedback 1:1</h2>
                <p className="text-gray-700">
                  Aqui temos um modelo completo e estruturado de pauta para feedback 1:1, ideal para gestores aplicarem conversas de desenvolvimento de forma consistente, empática e produtiva. O formato é flexível: pode ser usado em encontros quinzenais ou mensais, com duração média de 30 a 45 minutos.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">🧩 Objetivo:</h3>
                <p className="text-gray-700">
                  Promover uma conversa de desenvolvimento individual, fortalecendo o vínculo gestor–colaborador, reconhecendo resultados, ajustando rotas e estimulando o crescimento contínuo.
                </p>
              </div>

              <div className="space-y-6">
                <div className="border-l-4 border-purple-500 pl-4 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">⏱️ 1. Abertura e acolhimento (5 minutos)</h3>
                  <p className="text-gray-700 mb-2"><strong>Objetivo:</strong> criar conexão e reduzir a tensão natural do feedback.</p>
                  <p className="text-gray-700 font-semibold mb-1">Perguntas sugeridas:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Como você tem se sentido nos últimos dias em relação ao trabalho?</li>
                    <li>• Algo fora do comum tem impactado a sua rotina?</li>
                    <li>• O que tem te motivado ou desafiado ultimamente?</li>
                  </ul>
                  <p className="text-sm text-gray-600 italic mt-2">💡 Dica do gestor: mantenha tom empático, postura aberta e linguagem corporal receptiva (olhar, inclinação leve, tom de voz neutro e calmo).</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">🧠 2. Revisão de entregas e resultados (10 minutos)</h3>
                  <p className="text-gray-700 mb-2"><strong>Objetivo:</strong> reconhecer conquistas e identificar o que precisa de reforço.</p>
                  <p className="text-gray-700 font-semibold mb-1">Guia prático:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Destaque 2 a 3 pontos positivos observados desde o último encontro.</li>
                    <li>• Cite evidências concretas (dados, comportamentos observáveis).</li>
                    <li>• Traga 1 ou 2 pontos que merecem melhoria.</li>
                  </ul>
                  <p className="text-gray-700 font-semibold mb-1 mt-3">Perguntas:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Do que você mais se orgulha desde nosso último 1:1?</li>
                    <li>• Há algo que você sente que poderia ter feito diferente?</li>
                    <li>• Quais resultados ou entregas você gostaria de destacar?</li>
                  </ul>
                  <div className="bg-gray-50 p-3 rounded mt-3">
                    <p className="text-sm text-gray-700">💬 <strong>Modelo SBI (Situação, Comportamento, Impacto)</strong> pode ser usado aqui:</p>
                    <p className="text-sm text-gray-600 italic">"Na reunião com o cliente (situação), percebi que você trouxe argumentos técnicos muito consistentes (comportamento), o que reforçou a credibilidade da equipe (impacto)."</p>
                  </div>
                </div>

                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">🚀 3. Desenvolvimento e futuro (15 minutos)</h3>
                  <p className="text-gray-700 mb-2"><strong>Objetivo:</strong> discutir evolução profissional, habilidades e PDI (Plano de Desenvolvimento Individual).</p>
                  <p className="text-gray-700 font-semibold mb-1">Perguntas orientadoras:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Em quais habilidades você gostaria de se desenvolver mais?</li>
                    <li>• Que tipo de tarefa ou projeto te ajudaria a crescer?</li>
                    <li>• Existe algo que eu possa fazer para facilitar esse desenvolvimento?</li>
                    <li>• Como você avalia seu progresso nas metas combinadas?</li>
                  </ul>
                  <p className="text-sm text-gray-600 italic mt-2">💡 Dica: aqui o gestor pode introduzir o Feedforward — sugerir ações futuras ao invés de focar apenas no passado.</p>
                </div>

                <div className="border-l-4 border-yellow-500 pl-4 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">📈 4. Alinhamento e compromissos (10 minutos)</h3>
                  <p className="text-gray-700 mb-2"><strong>Objetivo:</strong> transformar a conversa em plano de ação prático.</p>
                  <p className="text-gray-700 font-semibold mb-1">Checklist:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Definir até 3 ações concretas (quem fará, o que, até quando).</li>
                    <li>• Conectar as ações ao propósito do colaborador e ao plano de carreira.</li>
                    <li>• Confirmar entendimento mútuo ("Estamos alinhados em relação a esses próximos passos?").</li>
                  </ul>
                  <div className="bg-gray-50 p-3 rounded mt-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">🗂️ Exemplo de registro:</p>
                    <table className="w-full text-sm text-gray-700 border-collapse">
                      <thead className="bg-gray-200">
                        <tr>
                          <th className="border p-2">Ação</th>
                          <th className="border p-2">Responsável</th>
                          <th className="border p-2">Prazo</th>
                          <th className="border p-2">Observação</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border p-2">Participar de treinamento de comunicação assertiva</td>
                          <td className="border p-2">Colaborador</td>
                          <td className="border p-2">30 dias</td>
                          <td className="border p-2">RH apoiará na inscrição</td>
                        </tr>
                        <tr>
                          <td className="border p-2">Apresentar mini projeto de melhoria no processo de X</td>
                          <td className="border p-2">Colaborador</td>
                          <td className="border p-2">15 dias</td>
                          <td className="border p-2">Gestor fará mentoria quinzenal</td>
                        </tr>
                        <tr>
                          <td className="border p-2">Realizar acompanhamento semanal das metas</td>
                          <td className="border p-2">Gestor</td>
                          <td className="border p-2">Contínuo</td>
                          <td className="border p-2">Revisar indicadores na próxima 1:1</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border-l-4 border-pink-500 pl-4 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">💬 5. Encerramento (5 minutos)</h3>
                  <p className="text-gray-700 mb-2"><strong>Objetivo:</strong> fechar de forma positiva e inspiradora.</p>
                  <p className="text-gray-700 font-semibold mb-1">Perguntas e frases de fechamento:</p>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Como você se sentiu com essa conversa?</li>
                    <li>• Há algo que eu poderia fazer diferente nos próximos feedbacks?</li>
                    <li>• "Agradeço pela transparência e dedicação. Confio muito no seu potencial."</li>
                  </ul>
                  <p className="text-sm text-gray-600 italic mt-2">💡 Dica: o fechamento deve reforçar segurança psicológica — o colaborador precisa sair motivado, não pressionado.</p>
                </div>

                <div className="border-l-4 border-indigo-500 pl-4 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">📄 6. Registro e acompanhamento</h3>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Registre o resumo da conversa (principais pontos, compromissos e próximos passos).</li>
                    <li>• Registre no GridProfile.</li>
                    <li>• Revise o progresso na próxima 1:1, valorizando avanços.</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OneOnOne90;
